/* OfferLadder — horizontal "rank of all 3 rungs" strip rendered under
 * the LiveAdvice offer_presentation hero card.
 *
 * The hero card already shows the LEAD offer in detail. The ladder
 * gives the agent peripheral vision: "if this lead doesn't land,
 * here's the fallback and last-resort priced out so you can pivot
 * without flipping panes."
 *
 * Data source: GET /api/calls/:id/eligible-offers — same priority
 * sort the bot uses, so rung[0] always matches whatever the advisor
 * picked as the hero's lead. ELIGIBLE_OFFERS_SQL excludes any offer
 * already granted on this call (90-day cooldown), so rung[1..N] are
 * the live, still-pitchable alternatives.
 *
 * Bill impact for each rung is computed locally with the same
 * applyOfferToMonthly() the OfferCard list uses, so the numbers don't
 * drift between the hero and the ladder.
 *
 * No-callId case (AdviceBench dev page) renders nothing — the bench
 * mounts cards in isolation without an eligible-offers source.
 */

import { useEffect, useState } from "react";

import { API_URL } from "../lib/api";
import {
  applyOfferToMonthly,
  describeTerms,
  type EligibleOffer,
  rungAt,
  RUNG_LABEL,
} from "./OfferCard";

type LadderResponse = {
  call_id: number;
  customer_id: number | null;
  offers: EligibleOffer[];
};

const MAX_RUNGS = 3;

export function OfferLadder({
  callId,
  currentMonthlyCents,
  presentedOfferCode,
}: {
  callId: number | null | undefined;
  currentMonthlyCents: number;
  presentedOfferCode: string;
}) {
  const [offers, setOffers] = useState<EligibleOffer[] | null>(null);

  useEffect(() => {
    if (!callId) {
      setOffers(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/calls/${callId}/eligible-offers`);
        if (!r.ok) {
          if (!cancelled) setOffers([]);
          return;
        }
        const data = (await r.json()) as LadderResponse;
        if (!cancelled) setOffers(data.offers ?? []);
      } catch {
        if (!cancelled) setOffers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [callId]);

  // No callId (AdviceBench) or fetch hasn't returned yet → render nothing
  // rather than an empty placeholder so the hero card sits flush.
  if (!callId || offers === null) return null;
  // Eligibility returned zero rows (rare — usually means every offer was
  // already granted). Hero already shows the recommendation; suppress
  // the empty strip.
  if (offers.length === 0) return null;

  const rungs = offers.slice(0, MAX_RUNGS);

  return (
    <div className="agent-desk-offer-ladder" aria-label="Offer ladder">
      <div className="agent-desk-offer-ladder-head">
        <span className="agent-desk-offer-ladder-title">Ladder</span>
        <span className="agent-desk-offer-ladder-sub">
          fallback options if the lead doesn't land
        </span>
      </div>
      <div className="agent-desk-offer-ladder-rungs">
        {rungs.map((o, i) => {
          const rung = rungAt(i);
          const { afterCents, freeMonthsApplied } = applyOfferToMonthly(
            currentMonthlyCents,
            o,
          );
          const isPresented = o.code === presentedOfferCode;
          const delta =
            currentMonthlyCents > 0 ? currentMonthlyCents - afterCents : 0;
          return (
            <div
              key={o.id}
              className={
                "agent-desk-offer-ladder-rung is-rung-" +
                rung +
                (isPresented ? " is-presented" : "")
              }
            >
              <div className="agent-desk-offer-ladder-rung-head">
                <span className={`agent-desk-offer-rung is-${rung}`}>
                  {RUNG_LABEL[rung]}
                </span>
                {isPresented && (
                  <span
                    className="agent-desk-offer-ladder-presented"
                    title="This is the offer the advisor surfaced in the hero card"
                  >
                    PRESENTED
                  </span>
                )}
              </div>
              <div className="agent-desk-offer-ladder-name">{o.name}</div>
              <div className="agent-desk-offer-ladder-terms">
                {describeTerms(o, o.upgrade_to_plan)}
              </div>
              <div className="agent-desk-offer-ladder-impact">
                {freeMonthsApplied ? (
                  <span className="agent-desk-offer-ladder-impact-free">
                    {o.free_months} month{o.free_months === 1 ? "" : "s"} free
                  </span>
                ) : (
                  <>
                    <span className="agent-desk-offer-ladder-impact-after">
                      ${(afterCents / 100).toFixed(0)}/mo
                    </span>
                    {delta > 0 && (
                      <span className="agent-desk-offer-ladder-impact-save">
                        ↓ ${(delta / 100).toFixed(0)}/mo
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
